import {
  Box,
  Divider,
  DrawerBody,
  DrawerHeader,
  InputGroup,
  Input,
  InputRightElement,
  useDisclosure,
  FormLabel,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { DrawerFixedButton, ControllableDrawer } from 'components/Drawer';
import { FormikInput, FormikRadio, FormikSelect, FormikSwitch } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import { hourOptions } from 'containers/CompanySettings/shared/options';
import FilterEmployeeAndGroupList from 'containers/Scheduling/RightDrawer/FilterEmployeeAndGroupList';
import { ISaveOvertimeAlerts, useSaveOvertimeAlerts } from 'hooks/companySettings/useSaveOvertimeAlerts';

import { initialOvertimeAlertData, overtimeAlertFormValidation } from './schema';

interface IOvertimeAlertForm {
  onClose: () => void;
  formData?: typeof initialOvertimeAlertData;
}

const OvertimeAlertForm: React.FC<IOvertimeAlertForm> = ({ onClose, formData = initialOvertimeAlertData }) => {
  const { t } = useTranslation(['common', 'companySettings']);

  const { status, mutateAsync } = useSaveOvertimeAlerts();

  const formikRef = useRef<any>(null);

  const {
    isOpen: isEmployeeListDrawerOpen,
    onOpen: onEmployeeListDrawerOpen,
    onClose: onEmployeeListDrawerClose,
  } = useDisclosure();

  const initialUserAndGroupData: any = {
    user_ids: formikRef?.current?.values?.user_ids || [],
    group_ids: formikRef?.current?.values?.group_ids || [],
  };

  const handleFormSubmit = (values: typeof initialOvertimeAlertData) => {
    const overallTime = values.alert_time_hour.value * 3600;

    const postData: ISaveOvertimeAlerts = {
      id: values.id,
      name: values.name,
      overtime_type: values.overtime_type,
      is_before: values.is_before === 'before' ? true : false,
      before_after_overtime: overallTime,
      user_ids: Array.isArray(values.user_ids) ? values.user_ids.map(user => user.userId) : [],
      send_notification_by_mail: values.send_notification_by_mail ? true : false,
      send_notification_by_push: values.send_notification_by_push ? true : false,
    };

    mutateAsync(postData).then(() => {
      if (onClose) {
        onClose();
      }
    });
  };

  const handleUserSelectFormSubmit = (values: typeof initialOvertimeAlertData) => {
    onEmployeeListDrawerClose();
    if (formikRef.current) {
      formikRef.current.setFieldValue('user_ids', values.user_ids);
      formikRef.current.setFieldValue('group_ids', values.group_ids);
    }
  };

  return (
    <>
      <DrawerHeader>{formData.id ? 'Update Overtime Alert' : 'Add Overtime Alert'}</DrawerHeader>
      <DrawerBody>
        <Divider width="100%" mb="6" />
        <Formik
          initialValues={formData}
          enableReinitialize
          onSubmit={handleFormSubmit}
          validationSchema={overtimeAlertFormValidation}
        >
          {formikBag => {
            formikRef.current = formikBag;
            const { values } = formikBag;

            return (
              <Form noValidate>
                <FormikInput type="text" name="name" label="Alert name" formControlProps={{ isRequired: true }} />
                {/* <Box mb={6}>
                <Box>
                  <FormikRadio
                    name="overtime_type"
                    label="Alert Type"
                    options={[
                      { label: 'Weekly', value: 'weekly' },
                      { label: 'Daily', value: 'daily' },
                    ]}
                    radioProps={{ mr: 3 }}
                    stackProps={{ direction: 'row', spacing: '10' }}
                    formControlProps={{ isRequired: true }}
                  />
                </Box>
              </Box> */}

                <Box mb={6}>
                  <FormikRadio
                    name="is_before"
                    label="Alert Occurrence"
                    options={[
                      { label: 'Before overtime', value: 'before' },
                      { label: 'After overtime', value: 'after' },
                    ]}
                    radioProps={{ mr: 3 }}
                    stackProps={{ direction: 'row', spacing: '10' }}
                    formControlProps={{ isRequired: true }}
                  />
                </Box>

                <FormikSelect
                  name="alert_time_hour"
                  label="Alert occurrence time(Hours)"
                  options={hourOptions.slice(1, -1)}
                  formControlProps={{ isRequired: true }}
                  selectProps={{
                    placeholder: t('companySettings:selectHours'),
                    isClearable: false,
                    size: 'md',
                  }}
                />

                <Box mb="6">
                  <FormLabel
                    textAlign="left"
                    textTransform="uppercase"
                    color="gray.500"
                    fontSize="12px"
                    fontWeight="600"
                  >
                    Assigned Users
                  </FormLabel>
                  <InputGroup>
                    <Input
                      placeholder={t('common:selectUsers')}
                      fontSize="sm"
                      value={`${values.user_ids.length || t('common:all')} ${
                        values.user_ids.length === 1 ? t('common:user') : t('common:users')
                      }`}
                      onChange={() => null}
                      pr="60px"
                      color={values.user_ids.length ? 'primary.900' : 'gray.400'}
                    />
                    <InputRightElement
                      width="60px"
                      children={
                        <Button size="xs" onClick={onEmployeeListDrawerOpen} colorScheme="secondary">
                          {t('common:select')}
                        </Button>
                      }
                    />
                  </InputGroup>
                </Box>

                <FormLabel textAlign="left" textTransform="uppercase" color="gray.500" fontSize="12px" fontWeight="600">
                  NOTIFY USERS
                </FormLabel>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm" flex="0 0 80%">
                    Send notification - via email
                  </FormLabel>
                  <FormikSwitch name="send_notification_by_mail"></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm" flex="0 0 80%">
                    Send notification - via mobile push notification
                  </FormLabel>
                  <FormikSwitch name="send_notification_by_push"></FormikSwitch>
                </Box>

                <DrawerFixedButton>
                  <Button colorScheme="secondary" type="submit" width="100%" size="lg" isLoading={status === 'loading'}>
                    {formData.id ? 'Update alert' : 'Add alert'}
                  </Button>
                </DrawerFixedButton>
              </Form>
            );
          }}
        </Formik>
      </DrawerBody>
      <ControllableDrawer isOpen={isEmployeeListDrawerOpen} onClose={onEmployeeListDrawerClose}>
        <Suspense fallback={<LogoLoader />}>
          <Formik enableReinitialize initialValues={initialUserAndGroupData} onSubmit={handleUserSelectFormSubmit}>
            {() => (
              <Form noValidate autoComplete="off">
                <FilterEmployeeAndGroupList buttonLabel="Select" />
              </Form>
            )}
          </Formik>
        </Suspense>
      </ControllableDrawer>
    </>
  );
};
export default OvertimeAlertForm;
